import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, Meta, SafeResourceUrl, Title } from '@angular/platform-browser';
import { environment } from 'src/environments/environment';

/**
 * PROTOTIPO /home-piloto-2: la casa real escaneada en Matterport.
 * Un solo iframe: en el banner corre solo (fly-in + recorrido guiado en loop, sin mouse)
 * y al "Entrar" ese MISMO visor se expande a pantalla completa con una guía encima.
 * Así no se recarga el modelo ni se pierde la posición.
 */
const MODELO = 'daTHxukBe25';
/** igual que la transición de .hp-casa--animando en el scss */
const DURACION_MS = 750;
const INTRO_MIN = 2200;
const INTRO_MAX = 6000;

export interface PasoGuia {
	titulo: string;
	texto: string;
	icono: 'arrastra' | 'camina' | 'maqueta' | 'plano';
}

@Component({
	selector: 'app-home-piloto-2',
	templateUrl: './home-piloto-2.component.html',
	styleUrls: ['./home-piloto-2.component.scss']
})
export class HomePiloto2Component implements OnInit, OnDestroy {
	@ViewChild('marco', { static: true }) marco!: ElementRef<HTMLDivElement>;
	@ViewChild('casa', { static: true }) casa!: ElementRef<HTMLDivElement>;
	urlCompra = environment.URL_MICROSITIO;
	portada = `https://my.matterport.com/api/v1/player/models/${MODELO}/thumb?width=1920`;

	/** ts=3 + lp=1: arranca solo el recorrido guiado y lo repite (si el modelo tiene highlight reel) */
	urlVisor: SafeResourceUrl;

	cargado = false;
	inmersivo = false;
	guiaVisible = false;
	paso = 0;

	pasos: PasoGuia[] = [
		{ icono: 'arrastra', titulo: 'Mira a tu alrededor', texto: 'Arrastra con el mouse (o con el dedo) para girar la vista 360°.' },
		{ icono: 'camina', titulo: 'Camina por la casa', texto: 'Haz clic en los círculos blancos del piso y avanzas hasta ahí.' },
		{ icono: 'maqueta', titulo: 'Sal y mírala completa', texto: 'Abajo, el botón de la casita te saca a ver la casa entera en 3D, como maqueta. Gírala.' },
		{ icono: 'plano', titulo: 'Salta a cualquier cuarto', texto: 'Con el botón de plano la ves desde arriba. Toca un cuarto y te lleva directo.' },
	];

	private temporizadores: any[] = [];

	constructor(private title: Title, private meta: Meta, sanitizer: DomSanitizer) {
		this.urlVisor = sanitizer.bypassSecurityTrustResourceUrl(
			`https://my.matterport.com/show?m=${MODELO}&play=1&lang=es&brand=0&title=0&help=0&mls=0&ts=3&lp=1`
		);
	}

	ngOnInit(): void {
		this.title.setTitle('Entra a la casa | Sorteo Anáhuac (prototipo)');
		this.meta.updateTag({ name: 'robots', content: 'noindex, nofollow' });

		// Intro: la cortina tapa la carga de Matterport; sube cuando el 3D ya se ve
		// (mínimo INTRO_MIN para que se lea la marca, máximo INTRO_MAX por si el iframe tarda).
		document.body.style.overflow = 'hidden';
		this.temporizadores.push(setTimeout(() => { this.minimoCumplido = true; this.intentarRevelar(); }, INTRO_MIN));
		this.temporizadores.push(setTimeout(() => this.revelar(), INTRO_MAX));
	}

	intro = true;
	revelado = false;
	private minimoCumplido = false;

	private intentarRevelar(): void {
		if (this.minimoCumplido && this.cargado) this.revelar();
	}

	revelar(): void {
		if (this.revelado) return;
		this.revelado = true;
		if (!this.inmersivo) document.body.style.overflow = '';
		this.temporizadores.push(setTimeout(() => (this.intro = false), 1300));
	}

	ngOnDestroy(): void {
		this.meta.removeTag('name="robots"');
		this.temporizadores.forEach(t => clearTimeout(t));
		document.body.style.overflow = '';
	}

	alCargar(): void {
		// load llega antes de que Matterport pinte el 3D; esperamos para no enseñar su pantalla de carga
		this.temporizadores.push(setTimeout(() => { this.cargado = true; this.intentarRevelar(); }, 1500));
	}

	/**
	 * El visor sale del marco y crece hasta llenar la pantalla (técnica FLIP):
	 * se fija en la posición exacta del marco y de ahí se anima a 100vw × 100vh.
	 */
	entrar(): void {
		if (this.inmersivo) return;
		const el = this.casa.nativeElement;
		this.fijarEn(el, this.marco.nativeElement.getBoundingClientRect(), false);
		void el.offsetWidth; // fuerza el layout antes de animar
		el.classList.add('hp-casa--animando');
		requestAnimationFrame(() => {
			Object.assign(el.style, { top: '0px', left: '0px', width: '100vw', height: '100vh', borderRadius: '0px' });
		});
		this.inmersivo = true;
		this.paso = 0;
		document.body.style.overflow = 'hidden';
		this.temporizadores.push(setTimeout(() => (this.guiaVisible = true), 800));
	}

	salir(): void {
		if (!this.inmersivo) return;
		const el = this.casa.nativeElement;
		this.inmersivo = false;
		this.guiaVisible = false;
		document.body.style.overflow = '';
		this.fijarEn(el, this.marco.nativeElement.getBoundingClientRect(), true);
		this.temporizadores.push(setTimeout(() => {
			el.classList.remove('hp-casa--animando');
			el.removeAttribute('style');
			el.style.backgroundImage = `url(${this.portada})`;
		}, DURACION_MS));
	}

	private fijarEn(el: HTMLElement, r: DOMRect, redondeo: boolean): void {
		Object.assign(el.style, {
			position: 'fixed', zIndex: '1999',
			top: r.top + 'px', left: r.left + 'px', width: r.width + 'px', height: r.height + 'px',
			borderRadius: redondeo ? '' : getComputedStyle(el).borderRadius,
		});
	}

	abrirGuia(): void {
		this.paso = 0;
		this.guiaVisible = true;
	}

	get terminoGuia(): boolean {
		return this.paso >= this.pasos.length;
	}

	@HostListener('document:keydown.escape')
	escape(): void {
		if (this.inmersivo) this.salir();
	}
}
